import React, {
  forwardRef,
  useCallback,
  useContext,
  useImperativeHandle,
} from 'react';
import { t } from 'i18next';
import { FormApi } from 'final-form';
import { Button } from 'ncoded-component-library';
import { Field, Form } from 'react-final-form';
import useCallbackRef from 'hooks/useCallbackRef';
import useHeightAnimation from 'hooks/useHeightAnimation';
import SideDrawer from '../../../SideDrawer';
import PopNotificationsContext from 'providers/PopNotifications/PopNotifications.context';
import { SideDrawerRef } from '../../../SideDrawer/SideDrawer.component';
import RadioGroupField from 'components/RadioGroupField';
import TextAreaField from 'components/TextAreaField';
import QAndAContext from 'router/subrouters/DataRoom/providers/QAndA/QAndA.context';
import dataRoomApi from 'router/subrouters/DataRoom/dataRoomApi';
import { QuestionPreview } from '../../../QuestionPreview';
import {
  DATA_ROOM_FAQ_ACCESS,
  FAQAccessForm,
} from 'router/subrouters/DataRoom/models/FAQ';
import { processFAQAccess } from '../../../FAQAccess/utils';
import { QuestionManagingDrawerCommonProps } from 'router/subrouters/DataRoom/models/QAndA';
import useGetFollowUpQuestion from '../../../QAndABuyerContainer/hooks/useGetFollowUpQuestion';
import { PreviewSideDrawerRef } from '../../../PreviewQuestionDrawer/PreviewQuestionDrawer.component';
import AddQuestionToFaq from '../../../AddQuestionToFaq';
import mixpanelService from 'services/mixpanelService';
import useGetMixpanelProps from 'hooks/useGetMixpanelProps';
import {
  ANSWER_REVIEW_OUTPUT,
  AnswerReviewInitiatedEventProps,
  APPROVAL_MODUS,
} from 'models/MixpanelEvents';
import { richTextJsonToPlainText } from 'components/RichTextEditorField/utils';
import DataRoomContext from 'router/subrouters/DataRoom/providers/DataRoom/DataRoom.context';

import './ManageAnswer.styles.scss';

type ManageAnsweDrawerProps = QuestionManagingDrawerCommonProps;

type ManageAnswerDrawerForm = {
  respond?: string;
  publishToFAQ?: boolean;
  action: 'reject' | 'approve';
} & FAQAccessForm;

const ManageAnswerDrawer: React.ForwardRefRenderFunction<
  SideDrawerRef,
  ManageAnsweDrawerProps
> = ({ question, onClose, onQuestionsUpdate }, ref) => {
  const [container, containerRef] = useCallbackRef<HTMLDivElement>();
  const [drawer, drawerRef] = useCallbackRef<PreviewSideDrawerRef>();

  const { isOwner } = useContext(DataRoomContext);
  const { faqAccessData, updateQuestion } = useContext(QAndAContext);
  const { popServerError, popSuccess } = useContext(PopNotificationsContext);

  useImperativeHandle(ref, () => drawer, [drawer]);

  const getMixpanelProps = useGetMixpanelProps();

  const { isLoading, displayedQuestions } = useGetFollowUpQuestion(
    question,
    drawer?.open,
  );

  useHeightAnimation({
    element: container,
    defaultOpen: true,
  });

  const onQuestionManage = useCallback(
    async (
      values: ManageAnswerDrawerForm,
      formApi: FormApi<ManageAnswerDrawerForm, Partial<ManageAnswerDrawerForm>>,
    ) => {
      try {
        mixpanelService.answerReviewInitiated(
          getMixpanelProps({
            questionId: question._id,
            questionContent: richTextJsonToPlainText(question.content),
            answerId: question.dataRoomAnswerId,
            answerContent: richTextJsonToPlainText(
              question.dataRoomAnswer?.content,
            ),
            addedToFAQ: values.publishToFAQ,
            approvalModus: APPROVAL_MODUS.MANUAL,
            questionOutput:
              values.action === 'approve'
                ? ANSWER_REVIEW_OUTPUT.ACCEPTED
                : ANSWER_REVIEW_OUTPUT.REJECTED,
            isOwner,
          }) as AnswerReviewInitiatedEventProps,
        );

        const { action, ...rest } = values;
        const newValues = processFAQAccess(rest, faqAccessData);

        await dataRoomApi.questionsAndAnswers[`${action}Answer`](
          question.dataRoomAnswerId,
          { ...rest, ...newValues },
        );
        onQuestionsUpdate?.();
        onClose?.();
        popSuccess(t(`${action}Answer`));
        formApi.restart();
      } catch (e) {
        popServerError(e, t('qaError'));
      }
    },
    [
      faqAccessData,
      question,
      isOwner,
      getMixpanelProps,
      onClose,
      onQuestionsUpdate,
      popServerError,
      popSuccess,
    ],
  );

  return (
    <SideDrawer
      ref={drawerRef}
      closeAction={onClose}
      isLoading={isLoading}
      className="qa-manage-answer"
    >
      <p className="drawer-title">{t('manageAnswer')}</p>

      <Form
        onSubmit={onQuestionManage}
        initialValues={{
          action: 'approve',
          publishToFAQ: false,
          faqAccess: DATA_ROOM_FAQ_ACCESS.ALL,
        }}
        render={({ handleSubmit, submitting, invalid, values: { action } }) => {
          return (
            <form onSubmit={handleSubmit} className="qa-manage-answer__form">
              <QuestionPreview
                question={question}
                displayedQuestions={displayedQuestions}
                updateQuestion={updateQuestion}
              />

              <RadioGroupField
                className="qa-manage-answer__action"
                options={[
                  {
                    label: t('manageAnswerSellOptions.acceptAnswer'),
                    value: 'approve',
                  },
                  {
                    label: t('manageAnswerSellOptions.rejectAnswer'),
                    value: 'reject',
                  },
                ]}
                name="action"
                direction="column"
              />

              <div ref={containerRef} className="textarea-container">
                {action === 'reject' ? (
                  <Field
                    className="qa-manage-answer__respond"
                    label={t('manageAnswerSellOptions.respondText')}
                    name="rejectAnswerRespond"
                    component={TextAreaField}
                    maxLength={2000}
                  />
                ) : (
                  <AddQuestionToFaq question={question} />
                )}
              </div>

              <Button type="submit" disabled={submitting || invalid}>
                {t('manageAnswerSellOptions.submitText')}
              </Button>
            </form>
          );
        }}
      />
    </SideDrawer>
  );
};

export default forwardRef(ManageAnswerDrawer);
